import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Search, Menu, X, Dices } from "lucide-react";
import { useStore } from "../contexts/StoreContext";
import { useLang } from "../i18n";

const Header = () => {
  const { itemCount, setCartOpen, wishlist } = useStore();
  const { lang, setLang, t } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      navigate(`/catalog?search=${encodeURIComponent(searchQ.trim())}`);
      setMobileOpen(false);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-amber-50/95 backdrop-blur-md border-b-4 border-slate-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="site-logo"
            className="flex items-center gap-2 shrink-0"
          >
            <span className="w-10 h-10 bg-sky-400 border-4 border-slate-900 rounded-full flex items-center justify-center cartoon-shadow-sm">
              <Dices className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
            </span>
            <span className="font-display font-bold text-xl sm:text-2xl text-slate-900 leading-none">
              Bottega<br className="sm:hidden" />
              <span className="text-pink-500"> di Nalise</span>
            </span>
          </Link>

          {/* Search desktop */}
          <form
            onSubmit={submitSearch}
            className="hidden md:flex flex-1 max-w-md relative"
          >
            <input
              data-testid="search-input"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              type="text"
              placeholder={t("nav.search_placeholder")}
              className="cartoon-input w-full pl-12 pr-4 py-2.5 text-sm"
            />
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-700 pointer-events-none" />
          </form>

          <nav className="hidden lg:flex items-center gap-1 font-bold text-slate-900">
            <Link
              to="/"
              data-testid="nav-home"
              className="px-3 py-2 rounded-full hover:bg-yellow-300 transition-colors"
            >
              {t("nav.home")}
            </Link>
            <Link
              to="/catalog"
              data-testid="nav-catalog"
              className="px-3 py-2 rounded-full hover:bg-yellow-300 transition-colors"
            >
              {t("nav.catalog")}
            </Link>
            <Link
              to="/regalo-perfetto"
              data-testid="nav-gift"
              className="px-3 py-2 rounded-full bg-pink-300 hover:bg-pink-200 border-2 border-slate-900 transition-colors"
            >
              🎁 {t("nav.gift")}
            </Link>
            <Link
              to="/chi-siamo"
              data-testid="nav-about"
              className="px-3 py-2 rounded-full hover:bg-yellow-300 transition-colors"
            >
              {t("nav.about")}
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <button
              data-testid="language-toggle"
              onClick={() => setLang(lang === "it" ? "en" : "it")}
              className="hidden sm:flex items-center gap-1 bg-white border-4 border-slate-900 rounded-full px-3 py-1.5 font-bold text-sm cartoon-shadow-sm hover:bg-yellow-300 transition-colors"
              title="Cambia lingua / Change language"
            >
              <span className={lang === "it" ? "text-pink-600" : "text-slate-500"}>IT</span>
              <span className="text-slate-400">/</span>
              <span className={lang === "en" ? "text-pink-600" : "text-slate-500"}>EN</span>
            </button>

            <Link
              to="/wishlist"
              data-testid="nav-wishlist"
              className="relative w-11 h-11 bg-white border-4 border-slate-900 rounded-full flex items-center justify-center cartoon-shadow-sm hover:bg-pink-100 transition-colors"
            >
              <Heart className="w-5 h-5 text-pink-500" strokeWidth={2.5} fill={wishlist.length ? "#ec4899" : "none"} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-pink-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-slate-900">
                  {wishlist.length}
                </span>
              )}
            </Link>

            <button
              data-testid="open-cart-button"
              onClick={() => setCartOpen(true)}
              className="relative w-11 h-11 bg-sky-400 border-4 border-slate-900 rounded-full flex items-center justify-center cartoon-shadow-sm hover:bg-sky-300 transition-colors"
            >
              <ShoppingBag className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
              {itemCount > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-2 -right-2 bg-yellow-400 text-slate-900 text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-slate-900"
                >
                  {itemCount}
                </span>
              )}
            </button>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden w-11 h-11 bg-white border-4 border-slate-900 rounded-full flex items-center justify-center cartoon-shadow-sm"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile */}
        {mobileOpen && (
          <div className="lg:hidden pb-4 pt-2 space-y-3" data-testid="mobile-menu">
            <form onSubmit={submitSearch} className="relative">
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                type="text"
                placeholder={t("nav.search_placeholder")}
                className="cartoon-input w-full pl-12 pr-4 py-2.5 text-sm"
              />
              <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-700 pointer-events-none" />
            </form>
            <div className="flex flex-col gap-2 font-bold">
              <Link to="/" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-full hover:bg-yellow-300">
                {t("nav.home")}
              </Link>
              <Link to="/catalog" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-full hover:bg-yellow-300">
                {t("nav.catalog")}
              </Link>
              <Link to="/regalo-perfetto" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-full bg-pink-200 hover:bg-pink-100 font-bold">
                🎁 {t("nav.gift")}
              </Link>
              <Link to="/chi-siamo" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-full hover:bg-yellow-300">
                {t("nav.about")}
              </Link>
              <button
                onClick={() => setLang(lang === "it" ? "en" : "it")}
                className="px-3 py-2 rounded-full hover:bg-yellow-300 text-left"
              >
                {lang === "it" ? "Switch to English" : "Cambia in Italiano"}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
